import { useId, useState } from 'react';
import { Sparkles, RotateCcw, Wand2 } from 'lucide-react';
import { statement } from '@tragfaehigkeit/core';
import {
  useWizardStore,
  type AssessmentDraft,
  type AssessmentScalarKey,
} from '../../lib/store/wizard';
import { STEPS, type DimensionKey } from '../hub/dimensions';

const DIMENSION_STEPS = STEPS.filter(
  (s): s is (typeof STEPS)[number] & { id: DimensionKey } => s.id !== 'triage',
);
import { checkAssessmentReadiness } from '../../lib/statement/assessmentsAdapter';
import { makeDemoAssessmentDraft } from '../../lib/statement/demoAssessments';
import { buildHeuristicsInput } from '../../lib/statement/heuristicsAdapter';
import { useLlmFeatureState } from '../../lib/llm/featureGate';
import { useLlmEditorContext } from '../../lib/llm/useLlmEditorContext';
import { LLM_FIELD_KEYS, type LlmFieldKey } from '../../lib/llm/buildLlmPrompt';
import { LlmSuggestionButton } from '../llm/LlmSuggestionButton';
import { LlmExperimentalBanner } from '../llm/LlmExperimentalBanner';
import { useConfirm } from '../ui/ConfirmDialog';
import { de } from '../../lib/i18n/de';
import type { ScoreResult } from '@tragfaehigkeit/core';
import type { PseudonymizationContext } from '../../lib/llm/pseudonymize';

const LLM_FIELD_KEY_SET: ReadonlySet<string> = new Set(LLM_FIELD_KEYS);

/** Pure helper für Tests: ist dieser Scalar-Key ein vom LLM unterstütztes Feld? */
export function isLlmField(key: string): key is LlmFieldKey {
  return LLM_FIELD_KEY_SET.has(key);
}

interface Props {
  caseId: string;
}

interface FieldDef {
  readonly key: AssessmentScalarKey;
  readonly label: string;
  readonly hint?: string;
  readonly minRows: number;
  readonly required: boolean;
}

const SCALAR_FIELDS: ReadonlyArray<FieldDef> = [
  {
    key: 'verdictLongParagraph',
    label: 'Verdict (1–2 Absätze)',
    hint: 'Die zentrale Aussage der Stellungnahme. Mündet in der Empfehlung.',
    minRows: 4,
    required: true,
  },
  {
    key: 'verdictReasoning',
    label: 'Begründung (2–3 Absätze)',
    hint: 'Wie kommt die Bewertung zustande? Verweis auf die Dimensionen.',
    minRows: 5,
    required: true,
  },
  {
    key: 'recommendationDetailed',
    label: 'Konkrete Empfehlungen (Aufzählung)',
    hint: 'Nummerierte Liste mit konkreten Schritten oder Auflagen.',
    minRows: 4,
    required: true,
  },
  {
    key: 'constraintDiscussion',
    label: 'Zumutbarkeit (§ 10 Abs. 1 SGB II)',
    hint: 'Familienverantwortung, Pflege, gesundheitliche Einschränkungen.',
    minRows: 3,
    required: true,
  },
  {
    key: 'riskIndicators',
    label: 'Risikoindikatoren (Aufzählung)',
    hint: 'Was spricht gegen Tragfähigkeit? Pro Punkt eine Zeile.',
    minRows: 3,
    required: true,
  },
  {
    key: 'strengthIndicators',
    label: 'Stärken (Aufzählung)',
    hint: 'Was spricht für Tragfähigkeit? Pro Punkt eine Zeile.',
    minRows: 3,
    required: true,
  },
  {
    key: 'conditionsParagraph',
    label: 'Auflagen (optional)',
    hint: 'Konkrete Auflagen, falls die Empfehlung „konditionierte Fortführung" ist.',
    minRows: 2,
    required: false,
  },
  {
    key: 'reevaluationProposal',
    label: 'Re-Evaluation (optional)',
    hint: 'Wann und mit welchen Unterlagen sollte erneut geprüft werden?',
    minRows: 2,
    required: false,
  },
];

const ASSESSMENT_LINE_FIELDS: ReadonlyArray<FieldDef> = [
  { key: 'revenueAssessment', label: 'Umsatz', minRows: 1, required: false },
  { key: 'costsAssessment', label: 'Kosten', minRows: 1, required: false },
  { key: 'profitAssessment', label: 'Gewinn', minRows: 1, required: false },
  { key: 'hourlyRateAssessment', label: 'Stundensatz', minRows: 1, required: false },
  { key: 'liquidityAssessment', label: 'Liquidität', minRows: 1, required: false },
  { key: 'runwayAssessment', label: 'Reichweite', minRows: 1, required: false },
  { key: 'utilizationAssessment', label: 'Auslastung', minRows: 1, required: false },
];

const SUGGESTION_FLASH_MS = 5000;

export function AssessmentEditor({ caseId }: Props) {
  const confirm = useConfirm();
  const caseState = useWizardStore((s) => s.cases[caseId]);
  const draft = caseState?.assessments;
  const setScalar = useWizardStore((s) => s.setAssessmentScalar);
  const setDim = useWizardStore((s) => s.setAssessmentDimension);
  const replace = useWizardStore((s) => s.replaceAssessments);
  const reset = useWizardStore((s) => s.resetAssessments);
  const ensureCase = useWizardStore((s) => s.ensureCase);

  const [flashMessage, setFlashMessage] = useState<string | null>(null);

  // LLM-Feature-Status (Doppel-Gate: DB-Flag + Mistral-API-Key)
  const llmState = useLlmFeatureState();
  const llmEditorCtx = useLlmEditorContext(caseId);

  if (!draft || !caseState) {
    ensureCase(caseId);
    return null;
  }

  const heuristicsInputBundle = buildHeuristicsInput(caseState);
  const heuristicsOutput = heuristicsInputBundle
    ? statement.buildHeuristicAssessments(heuristicsInputBundle.input)
    : null;
  const heuristicsAvailable = heuristicsOutput !== null;
  const heuristicsSourceLabel =
    heuristicsInputBundle?.source === 'demo' ? 'Demo-Datenbasis' : 'Wizard-Daten';

  // LLM-Button rendern wenn: Feature-Flag + Mistral-Key + Pseudonymisierungs-
  // Kontext geladen + Heuristik-Score verfügbar (Prompt braucht den Score).
  const llmAvailable =
    llmState.available &&
    llmEditorCtx.pseudonymizationContext !== null &&
    heuristicsOutput !== null;
  const scoreResultForLlm: ScoreResult | null = heuristicsInputBundle?.input.score ?? null;
  const pseudonymizationContext: PseudonymizationContext | null =
    llmEditorCtx.pseudonymizationContext;

  const readiness = checkAssessmentReadiness(draft);

  function flashSuggestion(message: string): void {
    setFlashMessage(message);
    window.setTimeout(() => {
      setFlashMessage((prev) => (prev === message ? null : prev));
    }, SUGGESTION_FLASH_MS);
  }

  async function applyScalarSuggestion(
    key: AssessmentScalarKey,
    currentValue: string,
  ): Promise<void> {
    if (!heuristicsOutput) return;
    const proposed = readScalarFromHeuristics(heuristicsOutput, key);
    if (proposed === null || proposed.trim().length === 0) {
      flashSuggestion('Vorschlag nicht möglich — bitte fachlich ergänzen.');
      return;
    }
    if (currentValue.trim().length > 0) {
      const proceed = await confirm({
        title: de.confirmDialog.overwriteTitle,
        body: de.confirmDialog.overwriteBody,
        confirmLabel: de.confirmDialog.overwriteConfirmLabel,
      });
      if (!proceed) return;
    }
    setScalar(caseId, key, proposed);
    flashSuggestion('Vorschlagstext eingefügt — bitte fachlich prüfen und ggf. anpassen.');
  }

  async function applyDimensionSuggestion(
    dimension: DimensionKey,
    target: 'short' | 'detail',
    currentValue: string,
  ): Promise<void> {
    if (!heuristicsOutput) return;
    const proposed = heuristicsOutput.dimensionTexts[dimension][target];
    if (proposed.trim().length === 0) {
      flashSuggestion('Vorschlag nicht möglich — bitte fachlich ergänzen.');
      return;
    }
    if (currentValue.trim().length > 0) {
      const proceed = await confirm({
        title: de.confirmDialog.overwriteTitle,
        body: de.confirmDialog.overwriteBody,
        confirmLabel: de.confirmDialog.overwriteConfirmLabel,
      });
      if (!proceed) return;
    }
    setDim(caseId, dimension, { [target]: proposed });
    flashSuggestion('Vorschlagstext eingefügt — bitte fachlich prüfen und ggf. anpassen.');
  }

  function applyAllSuggestions(): void {
    if (!heuristicsOutput || !draft) return;
    const next = mergeHeuristicsIntoDraft(draft, heuristicsOutput);
    replace(caseId, next);
    flashSuggestion(
      'Alle leeren Felder mit Vorschlägen gefüllt — bitte fachlich prüfen und ggf. anpassen.',
    );
  }

  return (
    <div className="space-y-12">
      {/* Toolbar */}
      <header className="flex flex-col gap-4 border-b border-stone-200 pb-6 md:flex-row md:items-end md:justify-between">
        <div className="max-w-prose">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            Stellungnahme · Bewertungstexte
          </p>
          <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-stone-900">
            EIGENE TEXTE ERFASSEN.
          </h2>
          <p className="mt-2 font-serif text-sm italic text-stone-500">
            Diese Texte fließen direkt in die generierte Stellungnahme. Pflichtfelder sind mit ★
            markiert; ohne sie bleibt die Stellungnahme im Demo-Modus.
          </p>
          {heuristicsAvailable && (
            <p className="mt-2 font-sans text-xs uppercase tracking-widest text-stone-500">
              Heuristische Vorschläge basieren auf {heuristicsSourceLabel}. Vorschläge sind Entwürfe
              — der Berater verantwortet jeden Text.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <ReadinessBadge readiness={readiness} />
          <button
            type="button"
            disabled={!heuristicsAvailable}
            onClick={() => {
              applyAllSuggestions();
            }}
            className="inline-flex items-center gap-2 rounded-sm border border-stone-900 bg-stone-900 px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-700 disabled:cursor-not-allowed disabled:border-stone-300 disabled:bg-stone-200 disabled:text-stone-400"
            title={
              heuristicsAvailable
                ? 'Füllt alle leeren Felder mit Heuristik-Vorschlägen.'
                : 'Heuristik nicht verfügbar — Wizard-Daten fehlen.'
            }
          >
            <Wand2 size={14} />
            Alle Vorschläge generieren →
          </button>
          <button
            type="button"
            onClick={() => {
              replace(caseId, makeDemoAssessmentDraft());
            }}
            className="inline-flex items-center gap-2 rounded-sm border border-stone-900 bg-white px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-900 transition-colors hover:bg-stone-900 hover:text-white"
          >
            <Sparkles size={14} />
            Beispieltexte einfügen
          </button>
          <button
            type="button"
            onClick={() => {
              void confirm({
                title: de.confirmDialog.resetAssessmentsTitle,
                body: de.confirmDialog.resetAssessmentsBody,
                confirmLabel: de.confirmDialog.resetAssessmentsConfirmLabel,
                tone: 'danger',
              }).then((proceed) => {
                if (proceed) reset(caseId);
              });
            }}
            className="inline-flex items-center gap-2 rounded-sm px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 transition-colors hover:text-red-700"
          >
            <RotateCcw size={14} />
            Zurücksetzen
          </button>
        </div>
      </header>

      {flashMessage && (
        <div
          role="status"
          className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 font-sans text-xs uppercase tracking-widest text-amber-900"
        >
          {flashMessage}
        </div>
      )}

      {/* EXPERIMENTAL-Warnung — nur wenn LLM-Flag aktiv + Mistral konfiguriert */}
      {llmState.available && <LlmExperimentalBanner />}

      {/* Pflicht- und Option-Felder */}
      <section className="space-y-8">
        <SectionHeader
          eyebrow="Verdict & Empfehlung"
          title="STELLUNGNAHME · KERN"
          description="Die zentralen Texte, die in jede Stellungnahme nach FW-BA-Standard gehören."
        />
        {SCALAR_FIELDS.map((f) => (
          <TextAreaField
            key={f.key}
            value={draft[f.key]}
            onChange={(v) => {
              setScalar(caseId, f.key, v);
            }}
            field={f}
            heuristicsAvailable={heuristicsAvailable}
            onApplySuggestion={() => {
              void applyScalarSuggestion(f.key, draft[f.key]);
            }}
            llmAvailable={llmAvailable && isLlmField(f.key)}
            llmContext={
              llmAvailable && pseudonymizationContext && scoreResultForLlm && isLlmField(f.key)
                ? {
                    fieldKey: f.key,
                    heuristicsContext: buildLlmHeuristicsContext(heuristicsOutput, f.key),
                    scoreResult: scoreResultForLlm,
                    pseudonymizationContext,
                    caseId: llmEditorCtx.caseIdForLog,
                  }
                : null
            }
            onLlmStatus={flashSuggestion}
          />
        ))}
      </section>

      {/* Bewertungs-Einzeiler */}
      <section className="space-y-6">
        <SectionHeader
          eyebrow="Kennzahlen-Bewertungen"
          title="EINZEILER ZU JEDER KENNZAHL"
          description="Kurze Einordnungen pro wirtschaftlicher Kennzahl. Optional, aber empfohlen."
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {ASSESSMENT_LINE_FIELDS.map((f) => (
            <ShortInputField
              key={f.key}
              value={draft[f.key]}
              onChange={(v) => {
                setScalar(caseId, f.key, v);
              }}
              field={f}
              heuristicsAvailable={heuristicsAvailable}
              onApplySuggestion={() => {
                void applyScalarSuggestion(f.key, draft[f.key]);
              }}
              llmAvailable={llmAvailable && isLlmField(f.key)}
              llmContext={
                llmAvailable && pseudonymizationContext && scoreResultForLlm && isLlmField(f.key)
                  ? {
                      fieldKey: f.key,
                      heuristicsContext: buildLlmHeuristicsContext(heuristicsOutput, f.key),
                      scoreResult: scoreResultForLlm,
                      pseudonymizationContext,
                      caseId: llmEditorCtx.caseIdForLog,
                    }
                  : null
              }
              onLlmStatus={flashSuggestion}
            />
          ))}
        </div>
        <ShortInputField
          value={draft.gapInterpretation}
          onChange={(v) => {
            setScalar(caseId, 'gapInterpretation', v);
          }}
          field={{
            key: 'gapInterpretation',
            label: 'Lücken-Interpretation',
            hint: 'Wird automatisch aus dem Score abgeleitet, wenn leer (z.B. „nicht in der Lage ist").',
            minRows: 1,
            required: false,
          }}
          heuristicsAvailable={heuristicsAvailable}
          onApplySuggestion={() => {
            void applyScalarSuggestion('gapInterpretation', draft.gapInterpretation);
          }}
          llmAvailable={llmAvailable}
          llmContext={
            llmAvailable && pseudonymizationContext && scoreResultForLlm
              ? {
                  fieldKey: 'gapInterpretation',
                  heuristicsContext: buildLlmHeuristicsContext(
                    heuristicsOutput,
                    'gapInterpretation',
                  ),
                  scoreResult: scoreResultForLlm,
                  pseudonymizationContext,
                  caseId: llmEditorCtx.caseIdForLog,
                }
              : null
          }
          onLlmStatus={flashSuggestion}
        />
      </section>

      {/* Sechs Dimensionen */}
      <section className="space-y-6">
        <SectionHeader
          eyebrow="Sechs Dimensionen"
          title="KURZ- UND DETAILTEXT JE DIMENSION"
          description="Ein Detailtext pro Dimension ist Pflicht. Der Kurztext ist die Überschrift im Bericht."
        />
        <div className="space-y-6">
          {DIMENSION_STEPS.map((d) => (
            <DimensionBlock
              key={d.id}
              dimensionKey={d.id}
              number={d.nummer}
              dimensionName={d.name}
              accentVar={d.accentVar}
              short={draft.dimensionTexts[d.id].short}
              detail={draft.dimensionTexts[d.id].detail}
              onShortChange={(v) => {
                setDim(caseId, d.id, { short: v });
              }}
              onDetailChange={(v) => {
                setDim(caseId, d.id, { detail: v });
              }}
              heuristicsAvailable={heuristicsAvailable}
              onApplyShortSuggestion={() => {
                void applyDimensionSuggestion(d.id, 'short', draft.dimensionTexts[d.id].short);
              }}
              onApplyDetailSuggestion={() => {
                void applyDimensionSuggestion(d.id, 'detail', draft.dimensionTexts[d.id].detail);
              }}
            />
          ))}
        </div>
      </section>

      <p className="border-t border-stone-200 pt-6 text-right font-sans text-xs uppercase tracking-widest text-stone-500">
        Gespeichert · {draft.lastEditedAt ? formatTime(draft.lastEditedAt) : 'noch nicht'}
      </p>
    </div>
  );
}

interface LlmFieldContext {
  readonly fieldKey: LlmFieldKey;
  readonly heuristicsContext: string;
  readonly scoreResult: ScoreResult;
  readonly pseudonymizationContext: PseudonymizationContext;
  readonly caseId: string | null;
}

interface TextAreaFieldProps {
  value: string;
  onChange: (v: string) => void;
  field: FieldDef;
  heuristicsAvailable: boolean;
  onApplySuggestion: () => void;
  /** True wenn LLM-Vorschlag verfügbar — rendert den zweiten Button. */
  llmAvailable?: boolean;
  /** Kontext für den LLM-Call. Nur required wenn llmAvailable. */
  llmContext?: LlmFieldContext | null;
  /** Toast-Callback vom Editor. */
  onLlmStatus?: (msg: string) => void;
}

function TextAreaField({
  value,
  onChange,
  field,
  heuristicsAvailable,
  onApplySuggestion,
  llmAvailable = false,
  llmContext = null,
  onLlmStatus,
}: TextAreaFieldProps) {
  const id = useId();
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={id}
          className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
        >
          {field.label}
          {field.required && <span className="ml-1 text-red-700">★</span>}
        </label>
        <div className="flex items-center gap-2">
          <SuggestionButton available={heuristicsAvailable} onClick={onApplySuggestion} />
          {llmAvailable && llmContext && (
            <LlmSuggestionButton
              fieldKey={llmContext.fieldKey}
              currentValue={value}
              heuristicsContext={llmContext.heuristicsContext}
              scoreResult={llmContext.scoreResult}
              pseudonymizationContext={llmContext.pseudonymizationContext}
              caseId={llmContext.caseId}
              onSuggest={(text) => {
                onChange(text);
              }}
              onStatus={(msg) => {
                onLlmStatus?.(msg);
              }}
            />
          )}
        </div>
      </div>
      {field.hint && <p className="mt-1 font-serif text-sm italic text-stone-500">{field.hint}</p>}
      <textarea
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        rows={field.minRows + 1}
        className="mt-2 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base leading-relaxed text-stone-900 transition-colors focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
      />
    </div>
  );
}

function ShortInputField({
  value,
  onChange,
  field,
  heuristicsAvailable,
  onApplySuggestion,
  llmAvailable = false,
  llmContext = null,
  onLlmStatus,
}: TextAreaFieldProps) {
  const id = useId();
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={id}
          className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
        >
          {field.label}
        </label>
        <div className="flex items-center gap-2">
          <SuggestionButton available={heuristicsAvailable} onClick={onApplySuggestion} />
          {llmAvailable && llmContext && (
            <LlmSuggestionButton
              fieldKey={llmContext.fieldKey}
              currentValue={value}
              heuristicsContext={llmContext.heuristicsContext}
              scoreResult={llmContext.scoreResult}
              pseudonymizationContext={llmContext.pseudonymizationContext}
              caseId={llmContext.caseId}
              onSuggest={(text) => {
                onChange(text);
              }}
              onStatus={(msg) => {
                onLlmStatus?.(msg);
              }}
            />
          )}
        </div>
      </div>
      {field.hint && <p className="mt-1 font-serif text-xs italic text-stone-500">{field.hint}</p>}
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
      />
    </div>
  );
}

interface SuggestionButtonProps {
  available: boolean;
  onClick: () => void;
}

function SuggestionButton({ available, onClick }: SuggestionButtonProps) {
  return (
    <button
      type="button"
      disabled={!available}
      onClick={onClick}
      title={
        available
          ? 'Heuristisch generierten Vorschlag in dieses Feld einfügen.'
          : 'Heuristik nicht verfügbar — Wizard-Daten fehlen.'
      }
      className="inline-flex items-center gap-1 rounded-sm border border-stone-300 bg-white px-2 py-1 font-sans text-[10px] font-medium uppercase tracking-widest text-stone-600 transition-colors hover:border-stone-900 hover:bg-stone-900 hover:text-white disabled:cursor-not-allowed disabled:border-stone-200 disabled:text-stone-300 disabled:hover:bg-white disabled:hover:text-stone-300"
    >
      <Wand2 size={11} />
      Vorschlag generieren →
    </button>
  );
}

interface DimensionBlockProps {
  dimensionKey: DimensionKey;
  number: string;
  dimensionName: string;
  accentVar: string;
  short: string;
  detail: string;
  onShortChange: (v: string) => void;
  onDetailChange: (v: string) => void;
  heuristicsAvailable: boolean;
  onApplyShortSuggestion: () => void;
  onApplyDetailSuggestion: () => void;
}

function DimensionBlock({
  dimensionKey,
  number,
  dimensionName,
  accentVar,
  short,
  detail,
  onShortChange,
  onDetailChange,
  heuristicsAvailable,
  onApplyShortSuggestion,
  onApplyDetailSuggestion,
}: DimensionBlockProps) {
  const shortId = useId();
  const detailId = useId();
  const isComplete = detail.trim().length > 0;
  return (
    <article
      data-dim={dimensionKey}
      className="border-t border-stone-200 pt-5 first:border-t-0 first:pt-0"
    >
      <header className="flex items-baseline gap-4">
        <span className="font-serif text-3xl font-black text-stone-200">{number}</span>
        <div className="flex-1">
          <h3 className="font-serif text-base font-bold uppercase tracking-wide text-stone-900">
            {dimensionName}
          </h3>
        </div>
        <div
          aria-hidden="true"
          className="h-6 w-6 rounded-sm"
          style={{ backgroundColor: accentVar, opacity: isComplete ? 0.6 : 0.2 }}
        />
      </header>
      <div className="mt-4 space-y-3 pl-12">
        <div>
          <div className="flex items-center justify-between gap-3">
            <label
              htmlFor={shortId}
              className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
            >
              Kurztext (Überschrift)
            </label>
            <SuggestionButton available={heuristicsAvailable} onClick={onApplyShortSuggestion} />
          </div>
          <input
            id={shortId}
            type="text"
            value={short}
            onChange={(e) => {
              onShortChange(e.target.value);
            }}
            placeholder="z.B. Solide Qualifikation und Branchenerfahrung"
            className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
          />
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <label
              htmlFor={detailId}
              className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
            >
              Detailtext (1–2 Absätze)<span className="ml-1 text-red-700">★</span>
            </label>
            <SuggestionButton available={heuristicsAvailable} onClick={onApplyDetailSuggestion} />
          </div>
          <textarea
            id={detailId}
            value={detail}
            onChange={(e) => {
              onDetailChange(e.target.value);
            }}
            rows={4}
            className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base leading-relaxed text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
          />
        </div>
      </div>
    </article>
  );
}

interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  description: string;
}

function SectionHeader({ eyebrow, title, description }: SectionHeaderProps) {
  return (
    <div>
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {eyebrow}
      </p>
      <h3 className="mt-1 font-serif text-xl font-bold uppercase tracking-tight text-stone-900">
        {title}
      </h3>
      <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">{description}</p>
    </div>
  );
}

function ReadinessBadge({ readiness }: { readiness: ReturnType<typeof checkAssessmentReadiness> }) {
  if (readiness.isReady) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-100 px-3 py-1 font-sans text-xs font-medium uppercase tracking-widest text-emerald-800">
        ✓ Vollständig
      </span>
    );
  }
  const total = readiness.missingScalars.length + readiness.missingDimensionDetails.length;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-sm bg-amber-100 px-3 py-1 font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
      Noch {total} Pflichtfeld{total === 1 ? '' : 'er'} offen
    </span>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// LLM-Heuristics-Kontext für den Prompt
// ---------------------------------------------------------------------------

/**
 * Baut einen kompakten Datenkontext für den Mistral-Prompt aus den
 * Heuristik-Outputs. Der Berater hat oft schon Heuristik-Vorschläge im
 * Feld; wir geben dem LLM den Kontext aller Top-Level-Texte, damit es
 * konsistent zur restlichen Stellungnahme antwortet.
 *
 * Aufruf-Pflicht des Aufrufers: das Resultat MUSS durch pseudonymize()
 * laufen, bevor es an Mistral geht. Branchen-/Score-Informationen sind
 * fachlich neutral und müssen nicht maskiert werden.
 *
 * Exportiert für Tests.
 */
export function buildLlmHeuristicsContext(
  heuristics: statement.HeuristicsOutput | null,
  fieldKey: LlmFieldKey,
): string {
  if (!heuristics) return '';
  const parts: string[] = [];

  // Immer mitgeben: die heuristische Vorbefüllung für DAS Feld (als
  // „Vorlage zum Verfeinern" für den LLM).
  const ownHeuristic = readHeuristicByLlmKey(heuristics, fieldKey);
  if (ownHeuristic.trim().length > 0) {
    parts.push(`Bisherige heuristische Vorbefüllung für dieses Feld:\n${ownHeuristic}`);
  }

  // Kontext: das Gesamt-Verdict + die Risiken — damit das LLM weiß, in welche
  // Richtung die Stellungnahme geht.
  if (fieldKey !== 'verdictLongParagraph' && heuristics.verdictLongParagraph.trim().length > 0) {
    parts.push(`Kontext (Verdict-Heuristik):\n${heuristics.verdictLongParagraph}`);
  }
  if (fieldKey !== 'riskIndicators' && heuristics.riskIndicators.trim().length > 0) {
    parts.push(`Kontext (Risiko-Indikatoren):\n${heuristics.riskIndicators}`);
  }
  if (fieldKey !== 'strengthIndicators' && heuristics.strengthIndicators.trim().length > 0) {
    parts.push(`Kontext (Stärken):\n${heuristics.strengthIndicators}`);
  }

  return parts.join('\n\n');
}

function readHeuristicByLlmKey(out: statement.HeuristicsOutput, key: LlmFieldKey): string {
  switch (key) {
    case 'verdictLongParagraph':
      return out.verdictLongParagraph;
    case 'verdictReasoning':
      return out.verdictReasoning;
    case 'recommendationDetailed':
      return out.recommendationDetailed;
    case 'constraintDiscussion':
      return out.constraintDiscussion;
    case 'riskIndicators':
      return out.riskIndicators;
    case 'strengthIndicators':
      return out.strengthIndicators;
    case 'revenueAssessment':
      return out.revenueAssessment;
    case 'costsAssessment':
      return out.costsAssessment;
    case 'profitAssessment':
      return out.profitAssessment;
    case 'hourlyRateAssessment':
      return out.hourlyRateAssessment;
    case 'liquidityAssessment':
      return out.liquidityAssessment;
    case 'runwayAssessment':
      return out.runwayAssessment;
    case 'utilizationAssessment':
      return out.utilizationAssessment;
    case 'gapInterpretation':
      return out.gapInterpretation;
    case 'conditionsParagraph':
      return out.conditionsParagraph;
    case 'reevaluationProposal':
      return out.reevaluationProposal;
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Heuristics → Draft Mapping
// ---------------------------------------------------------------------------

/**
 * Liest einen einzelnen Skalar-Vorschlag aus der Heuristik-Output.
 * Liefert null, wenn der Schlüssel kein heuristikbarer Top-Level-Text ist.
 */
function readScalarFromHeuristics(
  out: statement.HeuristicsOutput,
  key: AssessmentScalarKey,
): string | null {
  switch (key) {
    case 'verdictLongParagraph':
      return out.verdictLongParagraph;
    case 'verdictReasoning':
      return out.verdictReasoning;
    case 'recommendationDetailed':
      return out.recommendationDetailed;
    case 'constraintDiscussion':
      return out.constraintDiscussion;
    case 'riskIndicators':
      return out.riskIndicators;
    case 'strengthIndicators':
      return out.strengthIndicators;
    case 'revenueAssessment':
      return out.revenueAssessment;
    case 'costsAssessment':
      return out.costsAssessment;
    case 'profitAssessment':
      return out.profitAssessment;
    case 'hourlyRateAssessment':
      return out.hourlyRateAssessment;
    case 'liquidityAssessment':
      return out.liquidityAssessment;
    case 'runwayAssessment':
      return out.runwayAssessment;
    case 'utilizationAssessment':
      return out.utilizationAssessment;
    case 'gapInterpretation':
      return out.gapInterpretation;
    case 'conditionsParagraph':
      return out.conditionsParagraph;
    case 'reevaluationProposal':
      return out.reevaluationProposal;
    default:
      return null;
  }
}

/**
 * Merged die Heuristik-Vorschläge in den Draft. Nur leere Felder werden
 * gefüllt, befüllte Felder bleiben unangetastet.
 */
function mergeHeuristicsIntoDraft(
  draft: AssessmentDraft,
  out: statement.HeuristicsOutput,
): Omit<AssessmentDraft, 'lastEditedAt'> {
  const fillIfEmpty = (current: string, proposed: string): string =>
    current.trim().length === 0 ? proposed : current;

  return {
    revenueAssessment: fillIfEmpty(draft.revenueAssessment, out.revenueAssessment),
    costsAssessment: fillIfEmpty(draft.costsAssessment, out.costsAssessment),
    profitAssessment: fillIfEmpty(draft.profitAssessment, out.profitAssessment),
    hourlyRateAssessment: fillIfEmpty(draft.hourlyRateAssessment, out.hourlyRateAssessment),
    liquidityAssessment: fillIfEmpty(draft.liquidityAssessment, out.liquidityAssessment),
    runwayAssessment: fillIfEmpty(draft.runwayAssessment, out.runwayAssessment),
    utilizationAssessment: fillIfEmpty(draft.utilizationAssessment, out.utilizationAssessment),
    gapInterpretation: fillIfEmpty(draft.gapInterpretation, out.gapInterpretation),
    verdictLongParagraph: fillIfEmpty(draft.verdictLongParagraph, out.verdictLongParagraph),
    verdictReasoning: fillIfEmpty(draft.verdictReasoning, out.verdictReasoning),
    recommendationDetailed: fillIfEmpty(draft.recommendationDetailed, out.recommendationDetailed),
    conditionsParagraph: fillIfEmpty(draft.conditionsParagraph, out.conditionsParagraph),
    reevaluationProposal: fillIfEmpty(draft.reevaluationProposal, out.reevaluationProposal),
    constraintDiscussion: fillIfEmpty(draft.constraintDiscussion, out.constraintDiscussion),
    riskIndicators: fillIfEmpty(draft.riskIndicators, out.riskIndicators),
    strengthIndicators: fillIfEmpty(draft.strengthIndicators, out.strengthIndicators),
    dimensionTexts: {
      identity: {
        short: fillIfEmpty(draft.dimensionTexts.identity.short, out.dimensionTexts.identity.short),
        detail: fillIfEmpty(
          draft.dimensionTexts.identity.detail,
          out.dimensionTexts.identity.detail,
        ),
      },
      activity: {
        short: fillIfEmpty(draft.dimensionTexts.activity.short, out.dimensionTexts.activity.short),
        detail: fillIfEmpty(
          draft.dimensionTexts.activity.detail,
          out.dimensionTexts.activity.detail,
        ),
      },
      style: {
        short: fillIfEmpty(draft.dimensionTexts.style.short, out.dimensionTexts.style.short),
        detail: fillIfEmpty(draft.dimensionTexts.style.detail, out.dimensionTexts.style.detail),
      },
      resources: {
        short: fillIfEmpty(
          draft.dimensionTexts.resources.short,
          out.dimensionTexts.resources.short,
        ),
        detail: fillIfEmpty(
          draft.dimensionTexts.resources.detail,
          out.dimensionTexts.resources.detail,
        ),
      },
      health: {
        short: fillIfEmpty(draft.dimensionTexts.health.short, out.dimensionTexts.health.short),
        detail: fillIfEmpty(draft.dimensionTexts.health.detail, out.dimensionTexts.health.detail),
      },
      private: {
        short: fillIfEmpty(draft.dimensionTexts.private.short, out.dimensionTexts.private.short),
        detail: fillIfEmpty(draft.dimensionTexts.private.detail, out.dimensionTexts.private.detail),
      },
    },
  };
}
